import {Component, computed, effect, inject, input, output, signal, untracked} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Card} from 'primeng/card';
import {Router} from '@angular/router';
import {APP_ROUTES} from '../../../../../app.routes';
import {TenderDetailResponse, TenderPhaseType} from '@frontend/shared-api';
import {TabStatusEnum} from '../../../../../shared/tabs/tab-status.model';
import {TenderStatusUtils} from '../../../../../phase/tender-status.util';
import {TenderInvitationMailComponent} from '../tender-invitation-mail/tender-invitation-mail.component';
import {TenderDescriptionComponent} from '../tender-description/tender-description.component';
import {TenderDescriptionRfiComponent} from '../../rfi/tender-description-rfi/tender-description-rfi.component';
import {TenderConfirmationComponent} from '../tender-confirmation/tender-confirmation.component';
import {GenericQuestionnaireComponent} from '../../../../../shared/components/provider-questionnaire/generic-questionnaire/generic-questionnaire.component';
import {Button} from 'primeng/button';
import {WizardPhaseHeaderComponent} from '../wizard-phase-header/wizard-phase-header.component';
import {createTabManagementSignals} from '../../../../../shared/tabs/tab-utils';
import {TabStatusDirective} from '../../../../../shared/tabs/tab-status.directive';
import {TabsUnsavedRevealDirective} from '../../../../../shared/tabs/tabs-unsaved-reveal.directive';

const TAB_INDICES = {
  MAIL_PREVIEW: 0,
  PROJECT_DESCRIPTION: 1,
  DECISION: 2,
  QUESTIONNAIRE: 3,
} as const;

@Component({
  selector: 'app-tender-questionnaire-wizard',
  imports: [
    TranslocoPipe,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    Card,
    TabPanel,
    TenderInvitationMailComponent,
    TenderDescriptionComponent,
    TenderDescriptionRfiComponent,
    TenderConfirmationComponent,
    GenericQuestionnaireComponent,
    Button,
    WizardPhaseHeaderComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './tender-questionnaire-wizard.component.html',
})
export class TenderQuestionnaireWizardComponent {
  private readonly router = inject(Router);

  readonly tenderDetails = input.required<TenderDetailResponse>();
  readonly phase = input.required<TenderPhaseType>();
  readonly openDecision = input(false);

  readonly accepted = output<void>();

  protected readonly tenderId = computed(() => this.tenderDetails().tenderId);

  protected readonly isRfi = computed(() => this.phase() === TenderPhaseType.RFI);

  protected readonly tabManagement = createTabManagementSignals();
  protected readonly mailPreviewTabStatus = signal(TabStatusEnum.COMPLETED);
  protected readonly projectDescriptionTabStatus = signal(TabStatusEnum.COMPLETED);
  protected readonly decisionTabStatus = signal(TabStatusEnum.NOT_STARTED);
  protected readonly questionnaireTabStatus = signal(TabStatusEnum.NOT_STARTED);

  protected readonly showDecisionTab = computed(() =>
    TenderStatusUtils.canEditDecision(this.tenderDetails().status),
  );

  protected readonly showQuestionnaireTab = computed(() =>
    TenderStatusUtils.canEditQuestionnaire(this.tenderDetails().status),
  );

  constructor() {
    effect(() => {
      if (this.openDecision() && this.showDecisionTab()) {
        untracked(() => this.onTabChange(TAB_INDICES.DECISION));
      }
    });
  }

  get activeTabIndex() {
    return this.tabManagement.activeTabIndex.asReadonly();
  }

  onTabChange(tabIndex: string | number): void {
    this.tabManagement.onTabChange(tabIndex);
  }

  goToNextTab(): void {
    const currentIndex = this.activeTabIndex();

    let nextTabIndex: number = TAB_INDICES.PROJECT_DESCRIPTION;
    if (currentIndex && !this.showDecisionTab() && !this.showQuestionnaireTab()) {
      return;
    }

    if (currentIndex === TAB_INDICES.PROJECT_DESCRIPTION) {
      if (this.showDecisionTab()) {
        nextTabIndex = TAB_INDICES.DECISION;
      } else if (this.showQuestionnaireTab()) {
        nextTabIndex = TAB_INDICES.QUESTIONNAIRE;
      }
    }
    this.onTabChange(nextTabIndex);
  }

  onTenderAccepted(): void {
    this.accepted.emit();
    this.onTabChange(TAB_INDICES.QUESTIONNAIRE);
  }

  onTenderRejected(): void {
    this.router.navigate([APP_ROUTES.PROVIDER_TENDERS]);
  }

  onQuestionnaireStatusChange(status: TabStatusEnum): void {
    this.questionnaireTabStatus.set(status);
  }

  onQuestionnaireSent(): void {
    this.router.navigate([APP_ROUTES.PROVIDER_TENDERS]);
  }
}
